import { useMemo, useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import PublishIcon from '@mui/icons-material/Publish';
import NoteAddIcon from '@mui/icons-material/NoteAddOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  formatCivilDate,
  resolveFormattingLocale,
  useCreateDraftVersion,
  useVersionsWithDrafts,
  useViewLevel,
} from '@eis/core';
import {
  EmptyState,
  EntityHeader,
  ErrorState,
  PageLayout,
  RequirePermission,
  StatusChip,
  cardSurfaceSx,
} from '@eis/patterns';
import { insurancePermissions as p } from '../../../permissions';
import { manifestFormFrom } from '../../tarifs/model/manifest-form';
import { bareEnum, versionNameOf } from '../../tarifs/model/types';
import { lifecycleOf } from '../../tarifs/model/version-list';
import { VersionList } from '../../tarifs/ui/VersionList';
import { VersionFacts } from '../../tarifs/ui/VersionFacts';
import { ManifestLibrary } from '../../tarifs/ui/ManifestLibrary';
import { PublishWizard } from '../../tarifs/ui/PublishWizard';
import {
  groupTarifVersionManifestSchema,
  useGroupCoreConfigs,
  useGroupGeneration,
  useGroupTarif,
  usePublishGroupTarifVersion,
  useValidateGroupTarifVersion,
} from '../api/group-tarifs';
import { pinnedChildVersionErn, pinnedGroupCoreErn } from '../model/group-core';
import { DraftVersionEditor } from '../../tarif-authoring/ui/DraftVersionEditor';
import { VersionStatusActions } from '../../tarif-authoring/ui/VersionStatusActions';
import { ProvisionalRefMapField } from '../../tarif-authoring/ui/ProvisionalRefMapField';
import { PinnedDeckungCard } from '../../tarif-authoring/ui/deckung/overview/PinnedDeckungCard';
import { GroupCorePanel } from './GroupCorePanel';
import { GroupRefsField } from './ChildVersionPicker';

/**
 * One group generation: its versions, the child version each pinned, and the group_core each
 * carried.
 *
 * Structurally the single-tarif screen — same switcher, same artifact library, same wizard — with
 * two additions that are the whole reason the group layer exists (service summary §3.2):
 *
 *  - the CHILD PIN, stated on the version itself. It is an exact physical version (decision S-3),
 *    so "which product version is this Group Policy template built on" has one answer per version and
 *    the screen shows it rather than making a reader dig it out of the manifest.
 *  - the GROUP CORE panel: factors, aggregation, billing defaults. Read-only, because the group
 *    factor lives in that asset and never in the child config, and a published asset is immutable.
 */
export function GroupGenerationDetailPage({
  slug,
  generation,
}: {
  readonly slug: string;
  readonly generation: string;
}): ReactNode {
  const { t, i18n } = useTranslation('insurance');
  const navigate = useNavigate();
  const { isAdvanced } = useViewLevel();
  const locale = resolveFormattingLocale(i18n.language);
  const groupTarif = useGroupTarif(slug);
  const generationQuery = useGroupGeneration(slug, generation);
  const versionsQuery = useVersionsWithDrafts({ owner: 'group', slug, generation });
  const coresQuery = useGroupCoreConfigs(slug);
  /** The selected version's ERN — a draft has no number yet (SDD-016 D-V4). */
  const [selected, setSelected] = useState<string | null>(null);
  const [editingDraft, setEditingDraft] = useState<string | undefined>(undefined);
  const createDraft = useCreateDraftVersion();
  const [publishing, setPublishing] = useState(false);
  /**
   * The version the OPEN wizard was prefilled from, frozen for as long as it is open.
   *
   * The wizard takes its prefill as an initial value, so it has to be remounted when the version
   * it copies changes — but publishing CREATES a version, which changes `current` underneath the
   * dialog and would remount it at the exact moment it is showing what it wrote. Freezing the key
   * on open is what keeps "Veröffentlicht als …" on screen; the dialog is modal, so nothing else
   * can change the selection while it is up.
   */
  const [publishKey, setPublishKey] = useState('none');
  const validate = useValidateGroupTarifVersion();
  const publish = usePublishGroupTarifVersion();

  const versions = useMemo(() => versionsQuery.data ?? [], [versionsQuery.data]);
  const published = versions.filter((version) => lifecycleOf(version) !== 'DRAFT');
  const active = generationQuery.data?.generation;

  const current =
    versions.find((version) => version.ern === selected) ??
    versions.find((version) => lifecycleOf(version, active) === 'ACTIVE') ??
    published.at(-1);
  const currentLifecycle = current === undefined ? undefined : lifecycleOf(current, active);

  const childPin = pinnedChildVersionErn(current?.manifest);
  const corePin = pinnedGroupCoreErn(current?.manifest);
  const coreAsset = (coresQuery.data?.coreConfigs ?? []).find((asset) => asset.ern === corePin);

  /**
   * `tarifservice:fahrrad` → `fahrrad`. The picker needs the linked tarif's SLUG, and the group
   * tarif carries its ERN — the link is by ERN because that is what a digest is computed over
   * (decision S-5), and the slug is the last path segment of it.
   */
  const linkedTarifErn = groupTarif.data?.groupTarif?.tarifErn ?? '';
  const linkedTarifSlug = linkedTarifErn.slice(linkedTarifErn.lastIndexOf(':') + 1);

  if (generationQuery.isError || versionsQuery.isError) {
    return (
      <PageLayout>
        <ErrorState
          description={t('groupTarifs.error.generation')}
          details={(generationQuery.error ?? versionsQuery.error)?.message}
          onRetry={() => {
            void generationQuery.refetch();
            void versionsQuery.refetch();
          }}
        />
      </PageLayout>
    );
  }

  if (generationQuery.data === undefined) {
    return (
      <PageLayout header={<Skeleton variant="text" width={320} height={44} />}>
        <Skeleton variant="rounded" height={260} />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      header={
        <EntityHeader
          title={t('tarifs.generations.label', { name: active?.name ?? generation })}
          subtitle={active?.ern}
          icon={<LayersOutlinedIcon />}
          backTo={{
            label: slug,
            href: `/insurance/group-tarifs/${slug}`,
            onNavigate: (event) => {
              event.preventDefault();
              void navigate({ href: `/insurance/group-tarifs/${slug}` });
            },
          }}
          meta={[
            {
              label: t('tarifs.generations.validFromLabel'),
              value:
                active?.validFrom === undefined ? '—' : formatCivilDate(active.validFrom, locale),
            },
            {
              label: t('tarifs.generations.activeVersionLabel'),
              value:
                active?.activeVersionErn === undefined || active.activeVersionErn === ''
                  ? '—'
                  : active.activeVersionErn.slice(active.activeVersionErn.lastIndexOf('/') + 1),
            },
          ]}
          actions={
            <RequirePermission anyOf={[p.groupTariffs.publish]}>
              <Button
                variant="contained"
                startIcon={<PublishIcon />}
                disabled={versionsQuery.isPending}
                onClick={() => {
                  setPublishKey(current?.ern ?? 'none');
                  setPublishing(true);
                }}
              >
                {t('tarifs.publish.action')}
              </Button>
            </RequirePermission>
          }
        />
      }
    >
      <Alert severity="info">{t('tarifs.generations.inForceHint')}</Alert>

      {versionsQuery.isPending ? (
        <Skeleton variant="rounded" height={200} />
      ) : versions.length === 0 ? (
        <EmptyState description={t('tarifs.versions.empty')} />
      ) : (
        // Design v2's two-column version screen, one layer up (prompt 24 Phase 24.4).
        <Grid container spacing={2.5} sx={{ alignItems: 'flex-start' }}>
          <Grid size={{ xs: 12, md: 5, lg: 4 }}>
            <Paper
              variant="outlined"
              sx={(theme) => ({ ...cardSurfaceSx(theme), overflow: 'hidden' })}
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{ alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}
              >
                <Typography variant="h4" component="h2" id="version-switcher">
                  {t('tarifs.versions.listTitle', { generation: active?.name ?? generation })}
                </Typography>
                <RequirePermission anyOf={[p.groupTariffs.update]}>
                  <Button
                    variant="contained"
                    startIcon={<NoteAddIcon />}
                    // A draft starts from the version on screen (D-V4); its child pin may float on
                    // the major until publish freezes it (D-V5).
                    disabled={createDraft.isPending}
                    onClick={() => {
                      const basedOnErn =
                        current !== undefined && currentLifecycle !== 'DRAFT'
                          ? current.ern
                          : undefined;
                      createDraft.mutate(
                        {
                          owner: 'group',
                          slug,
                          generation,
                          ...(basedOnErn === undefined ? {} : { basedOnErn }),
                        },
                        {
                          onSuccess: (draft) => {
                            setSelected(draft.ern ?? null);
                            setEditingDraft(draft.draftId);
                          },
                        },
                      );
                    }}
                  >
                    {t('authoring.draft.new')}
                  </Button>
                </RequirePermission>
              </Stack>
              <Divider />
              <VersionList
                versions={versions}
                generation={active}
                selectedErn={current?.ern}
                onSelect={setSelected}
              />
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 7, lg: 8 }}>
            {current !== undefined && (
              <Stack spacing={2.5}>
                <Paper variant="outlined" sx={(theme) => ({ p: 2.5, ...cardSurfaceSx(theme) })}>
                  <Stack spacing={2}>
                    <Stack
                      direction="row"
                      spacing={2}
                      useFlexGap
                      sx={{
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Stack
                          direction="row"
                          spacing={1.5}
                          useFlexGap
                          sx={{ alignItems: 'center', flexWrap: 'wrap' }}
                        >
                          <Typography variant="h3" component="h2">
                            {`${slug} / ${active?.name ?? generation} / ${
                              currentLifecycle === 'DRAFT'
                                ? t('tarifs.versions.draftLabel')
                                : versionNameOf(current)
                            }`}
                          </Typography>
                          <StatusChip
                            status={currentLifecycle ?? 'PUBLISHED'}
                            label={
                              currentLifecycle === 'ACTIVE'
                                ? t('tarifs.versions.inForce')
                                : t(
                                    `tarifs.versions.statusValue.${currentLifecycle ?? 'PUBLISHED'}` as never,
                                  )
                            }
                          />
                        </Stack>
                        <VersionFacts
                          facts={[
                            [t('tarifs.versions.generation'), active?.name ?? generation],
                            [
                              t('tarifs.generations.validFromLabel'),
                              active?.validFrom === undefined
                                ? '—'
                                : formatCivilDate(active.validFrom, locale),
                            ],
                            [
                              t('tarifs.versions.kind'),
                              t(
                                `tarifs.releaseType.${bareEnum(current.releaseType, 'RELEASE_TYPE_') || 'UNSPECIFIED'}` as never,
                              ),
                            ],
                            // The pin chain, on the surface. An exact physical version, never a
                            // generation.
                            [t('groupTarifs.versions.childPin'), childPin],
                            ...(isAdvanced
                              ? ([
                                  [t('tarifs.versions.ern'), current.ern ?? '—'],
                                  [t('groupTarifs.versions.groupCorePin'), corePin],
                                  [t('tarifs.versions.publishedBy'), current.publishedBy ?? '—'],
                                ] as const)
                              : []),
                          ]}
                        />
                      </Box>
                      {/* The same decision one layer up, and the same weight (D-V3). */}
                      <VersionStatusActions
                        owner="group"
                        slug={slug}
                        generation={generation}
                        version={versionNameOf(current)}
                        lifecycle={currentLifecycle ?? 'PUBLISHED'}
                        onEditDraft={() => {
                          setEditingDraft(current.draftId);
                        }}
                      />
                    </Stack>
                    <ManifestLibrary embedded manifest={current.manifest} digest={current.digest} />
                  </Stack>
                </Paper>
                <GroupCorePanel asset={coreAsset} pinnedErn={corePin} />
                {/* The RESOLVED variations under this version's pins (SDD-018 §5, §6). */}
                <PinnedDeckungCard
                  owner="group"
                  ern={current.ern ?? ''}
                  manifest={current.manifest}
                  draft={currentLifecycle === 'DRAFT'}
                />
              </Stack>
            )}
          </Grid>
        </Grid>
      )}

      <RequirePermission anyOf={[p.groupTariffs.publish]}>
        <PublishWizard
          key={publishKey}
          title={t('groupTarifs.publish.title', { generation })}
          open={publishing}
          initialValues={manifestFormFrom(current?.manifest, 'PATCH')}
          baselines={published
            .map((version) => version.ern)
            .filter((ern): ern is string => ern !== undefined && ern !== '')}
          schema={groupTarifVersionManifestSchema}
          renderRefs={(props) => (
            <GroupRefsField linkedTarifSlug={linkedTarifSlug} groupTarifSlug={slug} {...props} />
          )}
          renderBlock={(props) => (
            <ProvisionalRefMapField
              {...props}
              manifests={published.map((version) => version.manifest)}
            />
          )}
          validate={validate}
          publish={publish}
          variablesOf={(manifest, { activate: activateNow }) => ({
            groupTarifSlug: slug,
            generation,
            manifest,
            activate: activateNow,
          })}
          preconditionReady={!generationQuery.isFetching}
          onClose={() => {
            setPublishing(false);
          }}
          onPublished={(ern) => {
            if (ern !== undefined && ern !== '') setSelected(ern);
          }}
        />
      </RequirePermission>

      <DraftVersionEditor
        owner="group"
        slug={slug}
        generation={generation}
        draftId={editingDraft}
        open={editingDraft !== undefined}
        onClose={() => {
          setEditingDraft(undefined);
        }}
        onPublished={(version) => {
          setSelected(version.ern ?? null);
        }}
      />
    </PageLayout>
  );
}
